<template>
  <PublicLayout
    variant="no-bg"
    :hero="false"
    breadcrumbs-in-top
    :ui="{
      main: 'pt-0!',
    }"
  >
    <UContainer>
      <SPBreadcrumbs dark :breadcrumbs="breadcrumbs" />

      <div class="flex w-full max-xl:flex-col gap-5">
        <!-- Загрузка -->
        <template v-if="pending">
          <SPSkeleton
            variant="card"
            :ui="{
              card: 'w-full!',
            }"
          />
          <SPSkeleton
            variant="card"
            :ui="{
              card: 'w-103 shrink-0',
            }"
          />
        </template>

        <!-- Не найдена -->
        <div
          v-else-if="notFound"
          class="w-full text-center py-20 max-xl:py-15 max-md:py-12"
        >
          <p class="text-neutral-light">Новость не найдена</p>
        </div>

        <!-- Контент -->
        <template v-else>
          <UCard>
            <h1 class="typo-heading-h3 mb-4">
              {{ pageData?.title }}
            </h1>

            <div class="flex gap-2 text-neutral-default mb-8">
              <UBadge
                v-if="pageData?.published_at"
                icon="i-local-calendar"
                variant="default"
              >
                {{ dayjs(pageData?.published_at).format('DD MMMM YYYY') }}
              </UBadge>

              <UBadge
                v-if="pageData?.views_count"
                icon="i-local-eye"
                variant="default"
              >
                {{ pageData.views_count }}
              </UBadge>

              <UBadge
                v-if="pageData?.description"
                icon="i-local-timer"
                variant="default"
              >
                {{ getReadingTime(pageData.description || '') }}
              </UBadge>
            </div>

            <img
              v-if="pageData?.image_url"
              class="rounded-[16px] mb-8"
              :src="pageData.image_url"
              alt="Изображение новости"
            />

            <article
              class="article mb-8 text-neutral-default max-xl:mb-4"
              v-html="pageData?.description"
            />

            <UButton
              class="ring-neutral-border-default font-medium text-neutral-default! max-xl:hidden"
              icon="i-local-chevron-left"
              variant="outline"
              color="white"
              :ui="{
                leadingIcon: 'text-neutral-default!',
              }"
              @click="router.back()"
            >
              Назад
            </UButton>
          </UCard>

          <UCard
            class="w-103 grow max-xl:w-full shrink-0 h-max"
            title="О новости"
            :ui="{
              header: 'border-0 pb-0 max-xl:hidden',
              body: 'max-xl:py-3 max-xl:pb-2!',
            }"
          >
            <div
              class="space-y-4 max-xl:space-y-0! mb-8 max-xl:flex max-xl:gap-2 max-xl:mb-0 max-md:flex-col"
            >
              <SmallCard
                :slug="pageData?.direction?.slug"
                class="h-auto"
                top="Направление спорта"
                direction-size="small"
                :bottom="pageData?.direction?.name"
                :to="`/directions/${pageData?.direction?.id}`"
                :ui="{
                  body: 'max-xl:p-3!',
                }"
              />
              <SmallCard
                icon="i-local-avatar"
                top="Автор"
                :bottom="pageData?.source.name"
                :to="`/schools/${pageData?.source.id}`"
                :ui="{
                  body: 'max-xl:p-3!',
                }"
              />
            </div>

            <UButton
              class="w-full ring-accent-default max-xl:hidden"
              icon="i-local-share"
              variant="outline"
              @click="share"
            >
              Поделиться
            </UButton>
          </UCard>

          <div class="xl:hidden flex items-center gap-4">
            <UButton
              class="ring-neutral-border-default font-medium text-neutral-default!"
              icon="i-local-chevron-left"
              variant="outline"
              :ui="{
                leadingIcon: 'text-neutral-default!',
              }"
              @click="router.back()"
            >
              Назад
            </UButton>
            <UButton
              class="ring-accent-default"
              icon="i-local-share"
              variant="outline"
              @click="share"
            >
              Поделиться
            </UButton>
          </div>
        </template>
      </div>
    </UContainer>

    <NewsAndEventsSection
      v-if="news?.length"
      title="Похожие новости"
      :news="news"
      :loading="pending"
      :ui="{
        direction: 'w-6! h-6!',
      }"
    />
  </PublicLayout>
</template>

<script setup lang="ts">
import { dayjs } from '~/shared/day'

import PublicLayout from '~/layouts/PublicLayout.vue'
import { copyToClipboard, getReadingTime } from '~/shared'
import SmallCard from '~/components/SmallCard.vue'
import NewsAndEventsSection from '~/components/main-page/NewsAndEventsSection.vue'
import SPSkeleton from '~/components/SPSkeleton.vue'

defineOptions({
  name: 'NewsDetail',
})

const router = useRouter()
const route = useRoute()
const id = route.params.id as string

const directoryStore = useDirectoryStore()
const { news } = storeToRefs(directoryStore)

const newsStore = useNewsStore()
const { pageData } = storeToRefs(newsStore)

const breadcrumbs = computed(() => [
  {
    name: 'Новости',
    to: '/news/',
  },
  {
    name: pageData.value?.title || '',
  },
])

const { pending: apiPending } = useAppAsyncData(`news-${id}`, async () => {
  await Promise.all([newsStore.fetchArticle(id), directoryStore.fetchNews()])
})

const pending = computed(() => apiPending.value)
const notFound = computed(() => !apiPending.value && !pageData.value)

const share = async () => {
  if (import.meta.client) {
    try {
      await copyToClipboard(window.location.href)
      const toast = useToast()
      toast.add({
        title: 'Ссылка скопирована в буфер обмена',
        color: 'success',
        icon: 'i-lucide-check',
      })
    } catch (error) {
      console.error(error)
    }
  }
}
</script>

<style lang="scss" scoped>
.article {
  font-family: Lato, sans-serif;
  @include typo('paragraph-md-regular');

  * {
    font-family: Lato, sans-serif;
  }
}
</style>
